/**
 * ФЛАГ ЦЕНТРАЛЬНОГО КВАРТАЛА в стиле жетонов строений. Неподвижный флаг — это сам
 * образец (developer_artifacts/field_flags/flags_sample.webp): объемное золотое древко
 * под наклоном и граненое полотнище, которое красится в цвет игрока. Слои из образца
 * вырезает developer_artifacts/scripts/buildFieldFlagArt.py.
 *
 * Все слои — кадр одного размера с центром в центре жетона и в точках его картинки,
 * поэтому они кладутся с масштабом самого жетона.
 *
 * У игрока, чей сейчас ход, флаг живой: полотнище идет волной от своей
 * формы на образце, гребни и впадины ловят свет. Вокруг яркий контур и свечение, такие
 * же, как у его отрядов (см. configs/activeGlow). Остальные флаги неподвижны и не светятся.
 *
 * Рисует scenes/FieldFlags.
 */

import {FIELD_FLAG_LAYER_TRIMS} from './fieldFlagTrims';

export const FIELD_FLAG_TEXTURE_KEYS = {
  // Полотнище: тень, контур и свечение идут волной вместе с ним
  CLOTH: 'field_flag_cloth',
  CLOTH_LIGHT: 'field_flag_cloth_light',
  SHADOW: 'field_flag_shadow',
  CONTOUR: 'field_flag_contour',
  GLOW: 'field_flag_glow',
  // Древко стоит на месте
  POLE: 'field_flag_pole',
  POLE_CONTOUR: 'field_flag_pole_contour',
  POLE_GLOW: 'field_flag_pole_glow',
  POLE_SHADOW: 'field_flag_pole_shadow',
};

export const FIELD_FLAG_TEXTURE_PATHS = {
  [FIELD_FLAG_TEXTURE_KEYS.CLOTH]: '/img/field_flags/cloth.681c9997.png',
  [FIELD_FLAG_TEXTURE_KEYS.CLOTH_LIGHT]: '/img/field_flags/cloth_light.d581f22f.png',
  [FIELD_FLAG_TEXTURE_KEYS.SHADOW]: '/img/field_flags/cloth_shadow.7ac89d3b.png',
  [FIELD_FLAG_TEXTURE_KEYS.CONTOUR]: '/img/field_flags/cloth_contour.d9576ce1.png',
  [FIELD_FLAG_TEXTURE_KEYS.GLOW]: '/img/field_flags/cloth_glow.b4fcec68.png',
  [FIELD_FLAG_TEXTURE_KEYS.POLE]: '/img/field_flags/pole.8492e645.png',
  [FIELD_FLAG_TEXTURE_KEYS.POLE_CONTOUR]: '/img/field_flags/pole_contour.4b300673.png',
  [FIELD_FLAG_TEXTURE_KEYS.POLE_GLOW]: '/img/field_flags/pole_glow.2093d6df.png',
  [FIELD_FLAG_TEXTURE_KEYS.POLE_SHADOW]: '/img/field_flags/pole_shadow.e03f004b.png',
};

// Где картинка каждого слоя лежит в кадре: картинки обрезаны по содержимому
export const FIELD_FLAG_TEXTURE_TRIMS = {
  [FIELD_FLAG_TEXTURE_KEYS.CLOTH]: FIELD_FLAG_LAYER_TRIMS.cloth,
  [FIELD_FLAG_TEXTURE_KEYS.CLOTH_LIGHT]: FIELD_FLAG_LAYER_TRIMS.cloth_light,
  [FIELD_FLAG_TEXTURE_KEYS.SHADOW]: FIELD_FLAG_LAYER_TRIMS.cloth_shadow,
  [FIELD_FLAG_TEXTURE_KEYS.CONTOUR]: FIELD_FLAG_LAYER_TRIMS.cloth_contour,
  [FIELD_FLAG_TEXTURE_KEYS.GLOW]: FIELD_FLAG_LAYER_TRIMS.cloth_glow,
  [FIELD_FLAG_TEXTURE_KEYS.POLE]: FIELD_FLAG_LAYER_TRIMS.pole,
  [FIELD_FLAG_TEXTURE_KEYS.POLE_CONTOUR]: FIELD_FLAG_LAYER_TRIMS.pole_contour,
  [FIELD_FLAG_TEXTURE_KEYS.POLE_GLOW]: FIELD_FLAG_LAYER_TRIMS.pole_glow,
  [FIELD_FLAG_TEXTURE_KEYS.POLE_SHADOW]: FIELD_FLAG_LAYER_TRIMS.pole_shadow,
};

// Неподвижный флаг снизу вверх: на поле (scenes/FieldFlags) и в значках интерфейса
// (FlagLayers). В цвет игрока красится только ткань
export const FIELD_FLAG_REST_LAYERS = [
  {textureKey: FIELD_FLAG_TEXTURE_KEYS.SHADOW},
  {textureKey: FIELD_FLAG_TEXTURE_KEYS.POLE_SHADOW},
  {textureKey: FIELD_FLAG_TEXTURE_KEYS.CLOTH, isTinted: true},
  {textureKey: FIELD_FLAG_TEXTURE_KEYS.CLOTH_LIGHT},
  {textureKey: FIELD_FLAG_TEXTURE_KEYS.POLE},
];

export const FIELD_FLAG = {
  // Сторона кадра в точках и масштаб: как у жетона (TOKEN_SCALE)
  frameSize: 440,
  scale: 0.23,
  // Полотнище в точках кадра: где оно крепится к древку и какой длины
  cloth: {leftPx: 150, lengthPx: 232},
  // Сколько узлов у сетки волны на весь кадр: чем больше, тем глаже волна
  segments: 20,
  // Волна на ткани, в единицах поля: на образце ткань уже изогнута, волна идет от этой формы
  wave: {
    periodMs: 1250,
    waves: 1.25,
    // Размах на свободном конце (у древка ткань закреплена)
    amplitude: 4.5,
    // Вторая, мелкая волна: без нее движение выглядит механическим
    rippleAmplitude: 0.7,
    rippleWaves: 2.9,
    ripplePeriodMs: 720,
  },
  // Гребни светлее, впадины темнее
  shading: {light: 0.2, dark: 0.22},
  // Блик на шаре и искры активного флага: положение в единицах поля от центра жетона
  finial: {x: -22, y: -31.4, sizeFactor: 0.62, tint: 0xfff4c8, minAlpha: 0.55, maxAlpha: 1, pulseMs: 900},
  sparks: {x: 8, y: 8, count: 4, periodMs: 1700, riseFactor: 0.7, spreadFactor: 0.42, sizeFactor: 0.2, tints: [0xfff0a8, 0xffc21a]},
};
